import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { IndianRupee, RefreshCw, Loader2, Copy, FileSpreadsheet, Image as ImageIcon, Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CAT_LABEL = {
  MARKETING: "Marketing", MARKETING_LITE: "Marketing lite", UTILITY: "Utility",
  AUTHENTICATION: "Authentication", AUTHENTICATION_INTERNATIONAL: "Auth intl",
  SERVICE: "Service", AI_PROVIDER: "AI provider", OTHER: "Other",
};
const catLabel = (k) => CAT_LABEL[k] || k.charAt(0) + k.slice(1).toLowerCase().replace(/_/g, " ");
const inr = (n) => `₹${(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const inrShort = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

export const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const fmtDate = (s) => new Date(`${s}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
export function presetRange(key) {
  const now = new Date();
  if (key === "this") return [iso(new Date(now.getFullYear(), now.getMonth(), 1)), iso(now)];
  if (key === "last") return [iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)), iso(new Date(now.getFullYear(), now.getMonth(), 0))];
  const from = new Date(now); from.setDate(from.getDate() - (key === "7" ? 6 : 29));
  return [iso(from), iso(now)];
}
const parseIso = (s) => new Date(`${s}T00:00:00`);

// Day picker that shows dates as 01 Sep 2026 (day first), unlike the browser's date box.
export function DateField({ label, value, onChange, minDate, maxDate }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <label className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500">{label}</label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className="mt-1 w-44 flex items-center justify-between gap-2 border border-white/20 bg-white/5 text-white rounded-md px-3 py-2 text-sm hover:bg-white/10">
            {fmtDate(value)} <CalendarIcon className="h-4 w-4 text-slate-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            weekStartsOn={1}
            defaultMonth={parseIso(value)}
            selected={parseIso(value)}
            onSelect={(d) => { if (d) { onChange(iso(d)); setOpen(false); } }}
            disabled={[...(minDate ? [{ before: parseIso(minDate) }] : []), ...(maxDate ? [{ after: parseIso(maxDate) }] : [])]}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

const PRESETS = [["this", "This month"], ["last", "Last month"], ["7", "Last 7 days"], ["30", "Last 30 days"]];

// Turn day-level rows (already filtered to the chosen branch) into totals.
function summarize(rows, numbers, branchKey) {
  const per = {};
  numbers.forEach((n) => {
    if (branchKey === "all" || n.number === branchKey) per[n.number] = { branch_name: n.branch_name, number: n.number, cost: 0, volume: 0, categories: {} };
  });
  const categories = {}, days = {};
  let total_cost = 0, total_volume = 0;
  rows.forEach((r) => {
    if (branchKey !== "all" && r.number !== branchKey) return;
    const p = per[r.number] || (per[r.number] = { branch_name: r.branch_name, number: r.number, cost: 0, volume: 0, categories: {} });
    p.cost += r.cost; p.volume += r.volume;
    p.categories[r.category] = (p.categories[r.category] || 0) + r.cost;
    categories[r.category] = (categories[r.category] || 0) + r.cost;
    days[r.date] = (days[r.date] || 0) + r.cost;
    total_cost += r.cost; total_volume += r.volume;
  });
  return {
    total_cost, total_volume, categories,
    numbers: Object.values(per).sort((x, y) => y.cost - x.cost),
    daily: Object.entries(days).sort().map(([date, cost]) => ({ date, cost })),
  };
}

const GST = 0.18; // Meta adds 18% IGST on the invoice; Insights amounts are before GST
const withGst = (n) => (n || 0) * (1 + GST);
const rowName = (r) => r.branch_name || r.number || "Unknown";

function downloadCsv(data, monthLabel, ads) {
  const cats = Object.keys(data.categories);
  const lines = [["Branch", "Number", ...cats.map(catLabel), "Messages delivered", "Before GST (INR)", "With 18% GST (INR)"]];
  data.numbers.forEach((r) =>
    lines.push([rowName(r), r.number || "", ...cats.map((c) => r.categories[c] || 0), r.volume, r.cost, withGst(r.cost).toFixed(2)]));
  lines.push(["TOTAL", "", ...cats.map((c) => data.categories[c] || 0), data.total_volume, data.total_cost, withGst(data.total_cost).toFixed(2)]);
  if (ads) {
    lines.push([], ["ADS: card debits", "Date", "Time", "Amount charged (INR)", "Reference"]);
    ads.debits.forEach((d) => lines.push(["", d.date, d.time, d.amount, d.reference || ""]));
    lines.push(["Total charged", "", "", ads.debits_total, ""]);
    lines.push([], ["ADS: spent on", "Spend before GST (INR)", "Leads", "Cost per lead (INR)"]);
    ads.campaigns.forEach((c) => lines.push([c.name, c.spend, c.leads, c.cost_per_lead ?? ""]));
    lines.push(["Total spent on ads", ads.spend_total, ads.leads_total, ""]);
  }
  const csv = lines.map((l) => l.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `Meta bill ${monthLabel}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// White report image (1080 wide) — easy to forward on WhatsApp.
function downloadPng(data, monthLabel) {
  const W = 1080, P = 70, rowH = 70;
  const H = 590 + data.numbers.length * rowH + 120;
  const cv = document.createElement("canvas");
  cv.width = W; cv.height = H;
  const x = cv.getContext("2d");
  const font = (s, w = 400) => `${w} ${s}px "Segoe UI", Roboto, Arial, sans-serif`;
  x.fillStyle = "#fff"; x.fillRect(0, 0, W, H);
  x.fillStyle = "#ea580c"; x.fillRect(0, 0, W, 16);
  x.fillStyle = "#0f172a"; x.font = font(44, 800);
  x.fillText("Adiyash Gym — Meta Bill", P, 100);
  x.fillStyle = "#64748b"; x.font = font(30);
  x.fillText(monthLabel, P, 148);
  x.fillStyle = "#94a3b8"; x.font = font(24, 700);
  x.fillText("TOTAL SPEND (BEFORE GST)", P, 240);
  x.fillStyle = "#ea580c"; x.font = font(84, 800);
  x.fillText(inr(data.total_cost), P, 330);
  x.fillStyle = "#64748b"; x.font = font(26);
  x.fillText(`${data.total_volume.toLocaleString("en-IN")} messages delivered`, P, 376);
  x.fillStyle = "#0f172a"; x.font = font(30, 700);
  x.fillText(`GST 18%: ${inr(data.total_cost * GST)}   ·   Total with GST: ${inr(withGst(data.total_cost))}`, P, 430);

  let y = 520;
  x.fillStyle = "#94a3b8"; x.font = font(22, 700);
  x.fillText("BRANCH", P, y); x.textAlign = "right"; x.fillText("BEFORE GST", W - P, y); x.textAlign = "left";
  y += 20;
  data.numbers.forEach((r) => {
    x.strokeStyle = "#e2e8f0"; x.lineWidth = 2;
    x.beginPath(); x.moveTo(P, y); x.lineTo(W - P, y); x.stroke();
    x.fillStyle = "#0f172a"; x.font = font(34, 700);
    x.fillText(rowName(r), P, y + 46);
    x.textAlign = "right"; x.fillText(inr(r.cost), W - P, y + 46); x.textAlign = "left";
    y += rowH;
  });
  x.fillStyle = "#94a3b8"; x.font = font(22);
  x.fillText("Approximate, from Meta Insights (before 18% GST) — may differ slightly from the invoice.", P, H - 60);
  cv.toBlob((blob) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `Meta bill ${monthLabel}.png`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, "image/png");
}

export default function MetaBilling() {
  const [range, setRange] = useState(() => presetRange("last"));
  const [raw, setRaw] = useState(null);
  const [branchKey, setBranchKey] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [from, to] = range;

  const load = useCallback(async (refresh = false) => {
    if (!from || !to) return;
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/meta-billing", { params: { start: from, end: to, ...(refresh ? { refresh: true } : {}) } });
      setRaw(res.data);
    } catch (e) {
      setRaw(null);
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  // Ad money (card debits + spend per campaign). Loaded on its own so a problem here never hides the WhatsApp numbers.
  const [ads, setAds] = useState(null);
  const [adsError, setAdsError] = useState("");
  useEffect(() => {
    if (!from || !to) return undefined;
    let live = true;
    setAds(null); setAdsError("");
    api.get("/meta-billing/ads", { params: { start: from, end: to } })
      .then((res) => { if (live) setAds(res.data); })
      .catch((e) => { if (live) setAdsError(formatApiErrorDetail(e?.response?.data?.detail) || e.message); });
    return () => { live = false; };
  }, [from, to]);

  const data = useMemo(() => (raw ? summarize(raw.rows, raw.numbers, branchKey) : null), [raw, branchKey]);
  const monthLabel = `${fmtDate(from)} – ${fmtDate(to)}${branchKey === "all" ? "" : ` (${raw?.numbers.find((n) => n.number === branchKey)?.branch_name || branchKey})`}`;
  const cats = data ? Object.keys(data.categories).sort((a, b) => data.categories[b] - data.categories[a]) : [];
  const maxDay = data ? Math.max(1, ...data.daily.map((d) => d.cost)) : 1;

  const copyText = async () => {
    const lines = data.numbers.map((r) => `${rowName(r)} - ${Math.round(r.cost)}`);
    lines.push(`Total (before GST) - ${Math.round(data.total_cost)}`);
    lines.push(`GST 18% - ${Math.round(data.total_cost * GST)}`);
    lines.push(`Total with GST - ${Math.round(withGst(data.total_cost))}`);
    if (ads) {
      lines.push("", "ADS (Facebook + Instagram)", `Card debits - ${Math.round(ads.debits_total)} (${ads.debits.length} times)`);
      ads.debits.forEach((d) => lines.push(`  ${fmtDate(d.date)} - ${Math.round(d.amount)}`));
      lines.push(`Spent on ads - ${Math.round(ads.spend_total)}`);
      ads.campaigns.forEach((c) => lines.push(`  ${c.name} - ${Math.round(c.spend)}`));
    }
    try {
      await navigator.clipboard.writeText(`Meta bill ${monthLabel}\n${lines.join("\n")}`);
      toast.success("Copied. Paste it in WhatsApp.");
    } catch {
      toast.error("Could not copy.");
    }
  };

  const btn = "bg-white/10 hover:bg-white/15 text-white font-semibold rounded-md px-3 py-2 text-sm transition-all duration-150 active:scale-[0.97] flex items-center gap-2 disabled:opacity-50";
  const card = "bg-[#141414] border border-white/10 rounded-lg p-4";

  return (
    <div data-testid="meta-billing-page">
      <h1 className="font-heading text-4xl tracking-tight font-black text-white">Meta Billing</h1>
      <p className="text-sm text-slate-500 mt-1">What WhatsApp messages and ads cost on Meta, and what the money was for.</p>

      <div className="mt-6 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500">Branch</label>
          <Select value={branchKey} onValueChange={setBranchKey}>
            <SelectTrigger className="w-52 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All branches</SelectItem>
              {(raw?.numbers || []).map((n) => (
                <SelectItem key={n.number} value={n.number}>{n.branch_name || n.number}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DateField label="From" value={from} maxDate={to} onChange={(v) => setRange([v, to])} />
        <DateField label="To" value={to} minDate={from} maxDate={iso(new Date())} onChange={(v) => setRange([from, v])} />
        <button className={btn} onClick={() => load(true)} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh
        </button>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {PRESETS.map(([k, label]) => (
          <button key={k} className="text-xs font-semibold rounded-full px-3 py-1 bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10" onClick={() => setRange(presetRange(k))}>{label}</button>
        ))}
        {data && (
          <span className="ml-auto flex flex-wrap items-center gap-2">
            <button className={btn} onClick={copyText}><Copy className="h-4 w-4" /> Copy as text</button>
            <button className={btn} onClick={() => downloadCsv(data, monthLabel, ads)}><FileSpreadsheet className="h-4 w-4" /> CSV</button>
            <button className={btn} onClick={() => downloadPng(data, monthLabel)}><ImageIcon className="h-4 w-4" /> Image (.png)</button>
          </span>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-2">Up to 90 days at a time.</p>

      {error && <div className="mt-6 text-sm rounded-md p-3 border bg-red-500/10 border-red-500/30 text-red-400 break-words">Meta error: {error}</div>}
      {loading && !data && <p className="mt-8 text-slate-500 text-sm">Loading from Meta…</p>}

      {data && (
        <div className="mt-6 space-y-6 max-w-5xl">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className={card}>
              <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500 flex items-center gap-1"><IndianRupee className="h-3 w-3" /> Total spend (before GST)</p>
              <p className="text-3xl font-black text-orange-500 mt-1">{inr(data.total_cost)}</p>
              <p className="text-xs text-slate-400 mt-1">GST 18% {inr(data.total_cost * GST)} · <span className="font-semibold text-white">With GST {inr(withGst(data.total_cost))}</span></p>
            </div>
            <div className={card}>
              <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500">Messages delivered</p>
              <p className="text-3xl font-black text-white mt-1">{data.total_volume.toLocaleString("en-IN")}</p>
            </div>
            <div className={card}>
              <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500">By type</p>
              <div className="mt-1 space-y-0.5">
                {cats.length === 0 && <p className="text-sm text-slate-500">No charges</p>}
                {cats.map((c) => (
                  <div key={c} className="flex justify-between text-sm text-slate-300">
                    <span>{catLabel(c)}</span><span className="font-semibold text-white">{inr(data.categories[c])}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Ads: what was charged to the card and what it was spent on */}
          <div className={card} data-testid="meta-billing-ads">
            <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500">Ads (Facebook + Instagram) — not split by branch</p>
            {adsError && <p className="mt-3 text-sm text-red-400 break-words">Could not load ads: {adsError}</p>}
            {!ads && !adsError && <p className="mt-3 text-sm text-slate-500">Loading ads from Meta…</p>}
            {ads && (
              <div className="mt-3 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Charged to the card</p>
                    <p className="text-2xl font-black text-white">{inr(ads.debits_total)}</p>
                    <p className="text-xs text-slate-500">{ads.debits.length} {ads.debits.length === 1 ? "time" : "times"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Spent on ads (before GST)</p>
                    <p className="text-2xl font-black text-orange-500">{inr(ads.spend_total)}</p>
                    <p className="text-xs text-slate-500">{ads.leads_total} leads</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Not charged yet (balance)</p>
                    <p className="text-2xl font-black text-white">{inr(ads.account?.balance)}</p>
                    <p className="text-xs text-slate-500">Meta charges the card when this reaches its limit</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-bold text-white mb-2">What the ad money was spent on</p>
                  {ads.campaigns.length === 0 ? <p className="text-sm text-slate-500">No ad spend in these dates.</p> : (
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-slate-500 text-xs">
                        <th className="py-2 pr-4 font-semibold">Campaign</th>
                        <th className="py-2 pr-4 font-semibold text-right">Leads</th>
                        <th className="py-2 pr-4 font-semibold text-right">Per lead</th>
                        <th className="py-2 font-semibold text-right">Spent</th>
                      </tr></thead>
                      <tbody>
                        {ads.campaigns.map((c) => (
                          <tr key={c.name} className="border-t border-white/10 text-slate-300">
                            <td className="py-2 pr-4 font-semibold text-white">{c.name}</td>
                            <td className="py-2 pr-4 text-right">{c.leads}</td>
                            <td className="py-2 pr-4 text-right">{c.cost_per_lead ? inr(c.cost_per_lead) : "—"}</td>
                            <td className="py-2 text-right font-bold text-orange-500">{inr(c.spend)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div>
                  <p className="text-sm font-bold text-white mb-2">Card debits (these match the "Meta ads receipt" emails)</p>
                  {ads.debits_note && <p className="text-sm text-red-400 mb-2 break-words">Could not read the debits: {ads.debits_note}</p>}
                  {ads.debits.length === 0 && !ads.debits_note ? <p className="text-sm text-slate-500">No card debits in these dates.</p> : (
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-slate-500 text-xs">
                        <th className="py-2 pr-4 font-semibold">Date</th>
                        <th className="py-2 pr-4 font-semibold">Time</th>
                        <th className="py-2 pr-4 font-semibold">Reference</th>
                        <th className="py-2 font-semibold text-right">Charged</th>
                      </tr></thead>
                      <tbody>
                        {ads.debits.map((d, i) => (
                          <tr key={`${d.reference}-${i}`} className="border-t border-white/10 text-slate-300">
                            <td className="py-2 pr-4 font-semibold text-white">{fmtDate(d.date)}</td>
                            <td className="py-2 pr-4">{d.time}</td>
                            <td className="py-2 pr-4 text-xs text-slate-500">{d.reference}</td>
                            <td className="py-2 text-right font-bold text-white">{inr(d.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className={card}>
            <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500 mb-3">Per number</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 text-xs">
                    <th className="py-2 pr-4 font-semibold">Branch</th>
                    <th className="py-2 pr-4 font-semibold">Number</th>
                    {cats.map((c) => <th key={c} className="py-2 pr-4 font-semibold text-right">{catLabel(c)}</th>)}
                    <th className="py-2 pr-4 font-semibold text-right">Delivered</th>
                    <th className="py-2 pr-4 font-semibold text-right">Before GST</th>
                    <th className="py-2 font-semibold text-right">With GST</th>
                  </tr>
                </thead>
                <tbody>
                  {data.numbers.map((r) => (
                    <tr key={r.number} className="border-t border-white/10 text-slate-300">
                      <td className="py-2.5 pr-4 font-bold text-white">{rowName(r)}</td>
                      <td className="py-2.5 pr-4">{r.number}</td>
                      {cats.map((c) => <td key={c} className="py-2.5 pr-4 text-right">{inr(r.categories[c])}</td>)}
                      <td className="py-2.5 pr-4 text-right">{r.volume.toLocaleString("en-IN")}</td>
                      <td className="py-2.5 pr-4 text-right font-bold text-orange-500">{inr(r.cost)}</td>
                      <td className="py-2.5 text-right font-bold text-white">{inr(withGst(r.cost))}</td>
                    </tr>
                  ))}
                  <tr className="border-t border-white/20 text-white font-bold">
                    <td className="py-2.5 pr-4" colSpan={2}>Total</td>
                    {cats.map((c) => <td key={c} className="py-2.5 pr-4 text-right">{inr(data.categories[c])}</td>)}
                    <td className="py-2.5 pr-4 text-right">{data.total_volume.toLocaleString("en-IN")}</td>
                    <td className="py-2.5 pr-4 text-right text-orange-500">{inr(data.total_cost)}</td>
                    <td className="py-2.5 text-right text-white">{inr(withGst(data.total_cost))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className={card}>
            <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500 mb-3">Day by day</p>
            <div className="flex items-end gap-1 h-32">
              {data.daily.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col justify-end h-full group relative" title={`${d.date}: ${inr(d.cost)}`}>
                  <div className="bg-orange-500/80 group-hover:bg-orange-400 rounded-t" style={{ height: `${Math.max(2, (d.cost / maxDay) * 100)}%` }} />
                </div>
              ))}
            </div>
            <div className="flex justify-between text-[10px] text-slate-500 mt-1">
              <span>{data.daily[0]?.date.slice(8)}</span>
              <span>Peak {inrShort(maxDay)}/day</span>
              <span>{data.daily[data.daily.length - 1]?.date.slice(8)}</span>
            </div>
          </div>

          <p className="text-xs text-slate-500">Approximate, from Meta Insights (amounts before 18% GST). May differ slightly from the invoice. Only the shared WABA (4 branches) is included.</p>
        </div>
      )}
    </div>
  );
}
